/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Measure from 'react-measure';
import { classnames as cx, cssUtils, domUtils, lodash, propsUtils } from '@ba-ui-toolkit/utils';
import Collapse from '@ba-ui-toolkit/collapse';
import List, { ListItem } from '@ba-ui-toolkit/list';
import { FlexItem } from '@ba-ui-toolkit/flexlayout';
import Label from '@ba-ui-toolkit/label';
import getSpecs from './listDropdownHelperFunctions.js';
import styles from './ListDropdown.scss';
import labelStyles from '../label/Label.scss';

const dropdownClassName = styles.dropdownClassName.replace(/"/g, '');
const labelClassName = cx(labelStyles.labelClassName.replace(/"/g, ''));
let refListPointer = null;

// Utils
const { cssSize } = cssUtils;
const { IE11, getTransformedParentElement } = domUtils;
const { uniqueId } = lodash;
const { sanitize } = propsUtils;

export default class ListDropdown extends Component {
	static propTypes = {
		id: PropTypes.string,
		triggerNodeId: PropTypes.string,
		truncated: PropTypes.bool,
		menuStyle: PropTypes.bool,
		open: PropTypes.bool,
		customWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		size: PropTypes.string,
		options: PropTypes.arrayOf(
			PropTypes.shape({
				group: PropTypes.any,
				value: PropTypes.any.isRequired,
				label: PropTypes.string.isRequired,
			})
		).isRequired,
		grouped: PropTypes.bool,
		showAllList: PropTypes.bool,
		onChange: PropTypes.func,
		onScrollClose: PropTypes.func,
		name: PropTypes.string,
		selectedIndex: PropTypes.number,
		focusIndex: PropTypes.number,
		onActiveChange: PropTypes.func,
		wrapperClickListener: PropTypes.func,
		triggerNodeRenderer: PropTypes.func.isRequired,
		listItemContentRenderer: PropTypes.func,
		className: PropTypes.string,
		onKeyUp: PropTypes.func,
		onKeyDown: PropTypes.func,
		dropdownHeaderRenderer: PropTypes.func,
		dropdownFooterRenderer: PropTypes.func,
		onClose: PropTypes.func,
		style: PropTypes.object,
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		ariaLabel: PropTypes.string,
		virtualized: PropTypes.bool,
	};

	static defaultProps = {
		grouped: false,
		truncated: true,
		selectedIndex: -1,
		open: false,
		className: '',
		listItemContentRenderer: (props, thisProps) => {
			const { options, truncated, textDir } = thisProps;
			const { label, title } = options[props.index];

			return (
				<FlexItem
					shrink
					grow
					ref={refListPointer[label]}
					basis="auto"
					className={`${dropdownClassName}__menuItemInner`}
				>
					<Label
						ellipsis={truncated}
						label={label || ''}
						textDir={textDir}
						truncatedTextExtraOptions={{ title }}
					/>
				</FlexItem>
			);
		},
	};

	constructor(props) {
		super(props);
		this.currentGroup = null;

		this.state = {
			above: false,
			menuStyle: {},
			scrolled: false,
			focusIndex: props.selectedIndex,
		};

		this.listId = this.props.triggerNodeId || uniqueId('__selectList');

		// Create a map for refs on all dropdown options
		this.optionsRef = {};
		props.options.forEach(option => {
			this.optionsRef[option.label] = React.createRef();
		});
		refListPointer = this.optionsRef;
	}

	componentDidMount() {
		const { wrapperClickListener, onClose } = this.props;
		document.addEventListener('scroll', this.scrollHandler, true);
		window.addEventListener('resize', onClose);
		wrapperClickListener && this._wrapper.addEventListener('click', wrapperClickListener);
	}

	componentWillReceiveProps(nextProps) {
		const {
			open: nextOpen,
			focusIndex: nextFocusIndex,
			selectedIndex,
			onClose: nextClose,
		} = nextProps;
		const { open: curOpen, focusIndex: curFocusIndex, onClose } = this.props;

		if (curOpen && nextOpen && curFocusIndex !== nextFocusIndex) {
			this.setState({ focusIndex: nextFocusIndex });
			this._list && this._list.scrollAround(nextFocusIndex);
		} else if (!curOpen && nextOpen) {
			this.setState({ focusIndex: selectedIndex });
			this._list && this._hasScrolling() && this._list.scrollAround(selectedIndex);
		}

		if (onClose !== nextClose) {
			window.removeEventListener('resize', onClose);
			window.addEventListener('resize', nextClose);
		}

		this._computeMenuState(nextProps);
	}

	componentDidUpdate(prevProps) {
		const { focusIndex } = this.state;
		const { open, grouped } = this.props;
		// This is responsible for scrolling the list to the correct point, when the first item in a group is selected
		// Click the first item in a group in a grouped dropdown, and observe the list scrolls to show the group label first
		if (grouped && open && open !== prevProps.open && focusIndex >= 0) {
			// this._list.scrollTo(focusIndex);
			const currentItem = this._list._getItemNode(focusIndex);
			if (currentItem && currentItem.childElementCount > 1) {
				// if this is the first item in its group, scrollup to show the group name as well
				const groupLabel = currentItem.querySelector(`.${labelClassName}`);
				const groupLabelOffset = window.getComputedStyle(groupLabel).marginTop;
				this._list._node.scrollTop =
					this._list._node.scrollTop - parseInt(groupLabelOffset, 10);
			}
		}
	}

	componentWillUnmount() {
		const { wrapperClickListener, onClose } = this.props;
		document.removeEventListener('scroll', this.scrollHandler, true);
		window.removeEventListener('resize', onClose);
		wrapperClickListener && this._wrapper.removeEventListener('click', wrapperClickListener);
	}

	scrollHandler = e => {
		const { onScrollClose } = this.props;

		if (this._list && this._list._node !== e.target && e.target.contains(this._list._node)) {
			this.setState({ scrolled: true }, () => {
				onScrollClose && onScrollClose();
				this.setState({ scrolled: false });
			});
		}
	};

	_computeMenuState(props) {
		const { truncated } = props;
		const { _wrapper, _triggerNode } = this;
		const newState = {};
		const nb = _triggerNode
			? _triggerNode.getBoundingClientRect()
			: _wrapper.getBoundingClientRect();
		const listHeight = this._getListHeight();

		const style = {
			minWidth: _triggerNode ? _triggerNode.offsetWidth : _wrapper.offsetWidth,
			maxWidth:
				truncated && _triggerNode ? _triggerNode.offsetWidth : window.innerWidth - nb.left,
		};

		const nearestTransform = !IE11 && getTransformedParentElement(_wrapper);
		const targetHeight = nearestTransform
			? nearestTransform.getBoundingClientRect().height
			: window.innerHeight;

		const availableRoomBottom = window.innerHeight - nb.top - nb.height;
		const availableRoomTop = nb.top;

		// open down if the list can fit opening down or if the list cannot fit opening up and there's more room at the bottom
		if (availableRoomBottom < listHeight && availableRoomTop > availableRoomBottom) {
			const specs = getSpecs(_triggerNode, _wrapper, 'top');
			style.bottom = targetHeight - specs.top - nb.height;
			style.borderBottom = 'none';
			style.left = specs.left;
			newState.above = true;
		} else {
			const specs = getSpecs(_triggerNode, _wrapper, 'bottom');
			style.top = specs.top;
			style.borderTop = 'none';
			style.left = specs.left;
			newState.above = false;
		}

		newState.menuStyle = style;
		this.setState(newState);
	}

	_getNumGroup() {
		const { options, grouped } = this.props;
		let numGroup = 0;
		if (grouped && options.length >= 1) {
			const groupHeader = [options[0].group];
			options.forEach(option => {
				if (option.group !== groupHeader[groupHeader.length - 1]) {
					groupHeader.push(option.group);
				}
			});
			numGroup = groupHeader.length;
		}

		return numGroup;
	}

	_getListHeight() {
		const { size, options, showAllList } = this.props;

		const optionHeight = cssSize(size === 'small' ? 4 : 5);
		const optionsTotalHeight = optionHeight * options.length;
		const groupHeaderHeight = cssSize(4) * this._getNumGroup();

		if (showAllList) {
			return groupHeaderHeight + optionsTotalHeight;
		}

		return Math.min(optionHeight * 5, groupHeaderHeight + optionsTotalHeight);
	}

	_hasScrolling() {
		const { size, options, showAllList } = this.props;

		const optionHeight = cssSize(size === 'small' ? 4 : 5);
		const optionsTotalHeight = optionHeight * options.length;
		const groupHeaderHeight = cssSize(4) * this._getNumGroup();
		const defaultHeight = optionHeight * 5;

		return !!(!showAllList && defaultHeight < groupHeaderHeight + optionsTotalHeight);
	}

	// update the min width of the menu based on the current width of the menu such that as the user
	// scrolls and new menu items are drawn, the menu will never become smaller and only grow in width
	// TODO we actually need to know when the list width changes. Use Measure to detect width changes.
	_onMenuWidthChange = dimensions => {
		const { customWidth, open } = this.props;
		if (!customWidth && this._menuNode && open) {
			const { menuStyle } = this.state;
			if (menuStyle) {
				if (dimensions.width > menuStyle.minWidth) {
					this.setState({ menuStyle: { ...menuStyle, minWidth: dimensions.width } });
				}
			}
		}
	};

	_optionId(index) {
		return `${this.listId}_${index}`;
	}

	_menuItemRenderer = (i, props) => {
		const { options, grouped, listItemContentRenderer } = this.props;
		let groupName;
		if (grouped && this.currentGroup !== options[i].group) {
			groupName = (
				<Label
					className={`${dropdownClassName}__groupLabel`}
					label={options[i].group}
					type="caption"
				/>
			);
		}
		this.currentGroup = i !== options.length - 1 ? options[i].group : null;

		const { icon, onIconClick, ...others } = options[i];
		// NOTE: This might be changed if the structure of grouped select options are changed
		return (
			<div key={i}>
				{groupName}
				<ListItem
					data-tid={`ba-common-list-dropdown__listItem-${i}`}
					{...props}
					{...sanitize(others, null, 'label', 'group', 'value', 'title')}
					icon={icon}
					onIconClick={onIconClick}
					className={`${dropdownClassName}__menuItem`}
					id={this._optionId(i)}
				>
					{listItemContentRenderer(props, this.props)}
				</ListItem>
			</div>
		);
	};

	_onActiveChange = ({ index }) => {
		const { onActiveChange } = this.props;
		this.setState({ focusIndex: index });
		onActiveChange && onActiveChange(index);
	};

	_onSelectionChange = ({ index }) => {
		const { onChange, options, name } = this.props;
		onChange && onChange(options[index].value, name, index);
	};

	_isSelected = index => this.props.selectedIndex === index;

	_menuNodeRef = node => {
		this._menuNode = node;
	};
	_listRef = node => {
		this._list = node;
	};
	_wrapperRef = node => {
		this._wrapper = node;
	};
	_triggerRef = node => {
		this._triggerNode = node;
	};

	_onCollapseMouseDown = e => {
		// prevent input from loosing the focus at mousedown phase (prevent blur event)
		// event order: mousedown => blur => mouseup => click
		e.preventDefault();
	};

	_onCollapseMouseUp = e => {
		// if clicking/dragging on the scrollbar, not collapse the dropdown,
		// otherwise manually update the state and loose the focus of input at mouseup phase
		if (this._list._node !== e.target && e.target !== e.currentTarget) {
			this._triggerNode.blur();
			this.props.onClose();
		}
	};

	_setFocus = label => {
		refListPointer[label].current.focus();
	};

	render() {
		const {
			id,
			truncated,
			textDir,
			open,
			options,
			grouped,
			size,
			triggerNodeRenderer,
			className,
			customWidth,
			onKeyUp,
			onKeyDown,
			dropdownFooterRenderer,
			dropdownHeaderRenderer,
			style,
			ariaLabel,
			virtualized,
			...others
		} = this.props;
		const { above, menuStyle, focusIndex, scrolled } = this.state;

		const selectionProps = {
			selectionMode: 'single',
			isSelected: this._isSelected,
			onSelectionChange: this._onSelectionChange,
		};
		const wrapperProps = {
			...sanitize(others, ListDropdown),
			id,
			className: `${dropdownClassName}__wrapper ${className}`,
			style: { width: customWidth },
			onKeyUp,
			onKeyDown,
			ref: this._wrapperRef,
			onClick: e => e.stopPropagation(),
		};

		const collapseProps = {
			className: cx(dropdownClassName, { truncated, above, grouped, [size]: size }),
			isOpened: !!open,
			style: {
				...menuStyle,
				...style,
			},
			ref: this._menuNodeRef,
			autoUnmount: true,
			onMouseDown: this._onCollapseMouseDown,
			onMouseUp: this._onCollapseMouseUp,
			'aria-label': ariaLabel,
		};

		const measureProps = {
			onResize: this._onMenuWidthChange,
		};

		const listContainerProps = {
			style: { height: this._getListHeight() },
		};

		const listProps = {
			id: this.listId,
			tabIndex: -1,
			length: options.length,
			itemRenderer: this._menuItemRenderer,
			initialIndex: focusIndex,
			onClick: e => e.stopPropagation(),
			overflowX: IE11 ? 'hidden' : null,
			onActiveChange: this._onActiveChange,
			activeIndex: focusIndex,
			alwaysDisplayActive: true,
			ref: this._listRef,
			textDir,
			virtualized,
			...selectionProps,
		};

		const dropdownContent = (
			<Collapse {...collapseProps}>
				<Fragment>
					{dropdownHeaderRenderer && dropdownHeaderRenderer()}
					<Measure {...measureProps}>
						{({ measureRef }) => (
							<div ref={measureRef} {...listContainerProps}>
								<List {...listProps} />
							</div>
						)}
					</Measure>
					{dropdownFooterRenderer && dropdownFooterRenderer()}
				</Fragment>
			</Collapse>
		);

		return (
			<div {...wrapperProps}>
				{triggerNodeRenderer(this._triggerRef)}
				{!scrolled && dropdownContent}
			</div>
		);
	}
}
